import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/react/daygrid";
import interactionPlugin from "@fullcalendar/react/interaction";
import listPlugin from "@fullcalendar/react/list";
import classicThemePlugin from "@fullcalendar/react/themes/classic";
import timeGridPlugin from "@fullcalendar/react/timegrid";
import "@fullcalendar/react/skeleton.css";
import "@fullcalendar/react/themes/classic/palette.css";
import "@fullcalendar/react/themes/classic/theme.css";
import { Activity, ArrowRight, BrainCircuit, CalendarDays, CheckCircle2, ChevronDown, CircleAlert, Cloud, Eye, EyeOff, LoaderCircle, LockKeyhole, MessageCircleMore, Plus, RotateCcw, ShieldCheck, UsersRound } from "lucide-react";
import {
  WHATSAPP_CLEAR_CONFIRMATION_PHRASE,
  UpdateAgentBrainSettingsSchema,
  opsCalendarProjection,
  type AgentBrainSettings,
  type UpdateAgentBrainSettings,
  type AgentControlState,
  type AgentProposal,
  type BoardSnapshot,
  type BusinessOperatingModelVersion,
  type OpsCalendarScope,
  type WhatsAppClearResyncPreflight,
  type WorkItem
} from "@business-ops/domain";
import { api, ApiError, safeGoogleAuthorizationUrl, type ActivityEvent, type GoogleWorkspaceSettings } from "../api.js";
import { clientColor, clientName, formatTimeAgo, memberName } from "../helpers.js";
import { ProductWorkspaceSettings } from "../components/product-settings.js";
import { BusinessFirstSetup } from "../components/business-first-setup.js";
import { SummaryDeliverySettings } from "../components/summary-delivery-settings.js";
import { ColorMark, EmptyState } from "../components/ui.js";

type CalendarFilter = OpsCalendarScope;

export function businessModelFetchFailureState(error: unknown): "MISSING" | "ERROR" {
  return error instanceof ApiError && error.status === 409 && error.code === "BUSINESS_MODEL_NOT_CONFIGURED" ? "MISSING" : "ERROR";
}

export function businessModelResultIsCurrent(currentWorkspaceId: string, expectedWorkspaceId: string, model: BusinessOperatingModelVersion): boolean {
  return currentWorkspaceId === expectedWorkspaceId && model.workspaceId === expectedWorkspaceId;
}

// WHY [BOL-SELF-SERVE-WEB-003]: after the first profile save the server may
// have turned Luna on (R769). Say so only when a fresh brain read confirms it;
// an unreadable brain state says just what is certain. The old notice ("Agent
// activation is separate.") is false for an eligible business after R769.
export function firstSetupSavedNotice(modelCallsEnabled: boolean | null): string {
  return modelCallsEnabled === true ? "Business profile saved. Luna is ready." : "Business profile saved.";
}

export async function announceFirstSetupSaved({ readBrain, stillCurrent, applyBrain, notify }: {
  readBrain: () => Promise<AgentBrainSettings>;
  stillCurrent: () => boolean;
  applyBrain: (brain: AgentBrainSettings) => void;
  notify: (text: string) => void;
}): Promise<void> {
  let brain: AgentBrainSettings | null = null;
  try {
    brain = await readBrain();
  } catch {
    brain = null;
  }
  // The person may have switched business while the read was in flight.
  if (!stillCurrent()) return;
  if (brain) applyBrain(brain);
  notify(firstSetupSavedNotice(brain ? brain.modelCallsEnabled : null));
}

export interface CalendarViewContext {
  calendarScope: OpsCalendarScope;
  visibleStartDay: string;
  visibleEndDayExclusive: string;
  selectedDay: string;
  focusedWorkItemId: string | null;
}

export function calendarViewConfiguration(compact: boolean) {
  return compact
    ? {
        initialView: "dayGridMonth",
        headerToolbar: { left: "prev,next", center: "title", right: "today" },
        footerToolbar: false as const,
        buttons: {}
      }
    : {
        initialView: "dayGridMonth",
        headerToolbar: { left: "prev,next", center: "title", right: "today" },
        footerToolbar: false as const,
        buttons: {}
      };
}

type WorkCalendarEvent = {
  id: string;
  title: string;
  start: string;
  allDay: boolean;
  color: string;
  extendedProps: {
    workId: string;
    client: string;
    owner: string;
    color: string;
    kind: "ENGAGEMENT" | "DUE" | "CHECKPOINT" | "SCHEDULED_MESSAGE";
    dayKey: string;
  };
};

export function calendarRenderKey(compact: boolean, events: readonly WorkCalendarEvent[]): string {
  return `${compact ? "compact" : "wide"}:${JSON.stringify(events)}`;
}

const ACTIVE_CALENDAR_STATUSES = new Set<WorkItem["status"]>(["BACKLOG", "READY", "IN_PROGRESS", "INTERNAL_REVIEW", "CLIENT_REVIEW", "BLOCKED"]);

export function calendarDayKeyFromClickTarget(target: EventTarget | null): string | null {
  const closest = (target as { closest?: (selector: string) => { getAttribute(name: string): string | null } | null } | null)?.closest;
  if (typeof closest !== "function") return null;
  const dayKey = closest.call(target, "[data-date]")?.getAttribute("data-date") ?? null;
  return dayKey && /^\d{4}-\d{2}-\d{2}$/u.test(dayKey) ? dayKey : null;
}

export function calendarDayKeyAfterArrow(dayKey: string, key: string): string | null {
  const offset = ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number | undefined>)[key];
  if (offset === undefined || !/^\d{4}-\d{2}-\d{2}$/u.test(dayKey)) return null;
  const date = new Date(`${dayKey}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}

export function calendarWorkIdFromClickTarget(target: EventTarget | null): string | null {
  const closest = (target as { closest?: (selector: string) => { getAttribute(name: string): string | null } | null } | null)?.closest;
  if (typeof closest !== "function") return null;
  const workId = closest.call(target, "[data-work-id]")?.getAttribute("data-work-id")?.trim() ?? "";
  return workId || null;
}

export function calendarEvents(board: BoardSnapshot, filter: CalendarFilter, _now = new Date()): WorkCalendarEvent[] {
  const itemById = new Map(board.workItems.map((item) => [item.id, item]));
  return opsCalendarProjection({
    workItems: board.workItems,
    workAgentStates: board.workAgentStates ?? [],
    workspaceTimeZone: board.workspace.timezone,
    scope: filter
  }).flatMap((entry) => {
    const item = itemById.get(entry.workItemId);
    if (!item) return [];
    const color = clientColor(board, item);
    const common = {
      color,
      extendedProps: {
        workId: item.id,
        client: clientName(board, item),
        owner: memberName(board, item.assigneeId),
        color,
        kind: entry.kind,
        dayKey: workspaceDayKey(entry.occursAt, entry.timeZone)
      }
    };
    if (entry.kind === "ENGAGEMENT") {
      return [{
        ...common,
        id: `engagement:${item.id}`,
        title: item.title,
        start: entry.allDay ? common.extendedProps.dayKey : entry.occursAt,
        allDay: entry.allDay
      }];
    }
    if (entry.kind === "SCHEDULED_MESSAGE") {
      // WHY [BOL-SCHEDULE-CALENDAR-001]: Calendar is a projection of the
      // same approved execution job, never a second reminder record. Render
      // its workspace wall time and open the owning Work drawer for edit or
      // cancel. Falsifier: changing/cancelling the job leaves a stale event,
      // or clicking it opens anything except the canonical Work item.
      return [{
        ...common,
        id: `scheduled-message:${item.id}:${entry.sourceId ?? entry.occursAt}`,
        title: `Scheduled message · ${item.title}`,
        start: calendarWorkspaceTimedStart(entry.occursAt, entry.timeZone),
        allDay: false
      }];
    }
    const schedule = calendarEventSchedule(entry.occursAt, entry.timeZone);
    return [{
      ...common,
      id: `${entry.kind === "DUE" ? "due" : "checkpoint"}:${item.id}`,
      title: entry.kind === "CHECKPOINT" ? `Follow up · ${item.title}` : item.title,
      ...schedule
    }];
  });
}

export function calendarEventLabel(event: Pick<WorkCalendarEvent, "title" | "extendedProps">): string {
  return `Open ${event.title}. Client: ${event.extendedProps.client}. Owner: ${event.extendedProps.owner}.`;
}

export function calendarEventsForDay(events: readonly WorkCalendarEvent[], dayKey: string): WorkCalendarEvent[] {
  return events.filter((event) => event.extendedProps.dayKey === dayKey);
}

export const CALENDAR_DESKTOP_CELL_LIMIT = 2;
export const CALENDAR_COMPACT_CELL_LIMIT = 3;

export function calendarDayProjection(events: readonly WorkCalendarEvent[], dayKey: string, compact: boolean): {
  visibleEvents: WorkCalendarEvent[];
  overflowCount: number;
} {
  const dayEvents = calendarEventsForDay(events, dayKey);
  const limit = compact ? CALENDAR_COMPACT_CELL_LIMIT : CALENDAR_DESKTOP_CELL_LIMIT;
  return {
    visibleEvents: dayEvents.slice(0, limit),
    overflowCount: Math.max(0, dayEvents.length - limit)
  };
}

function calendarClockLabel(hourText: string, minuteText: string): string {
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) return "";
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

export function calendarEventTimeLabel(event: Pick<WorkCalendarEvent, "start" | "allDay">, timeZone: string): string {
  if (event.allDay) return "";
  const localWallTime = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?$/u.exec(event.start);
  if (localWallTime) return calendarClockLabel(localWallTime[1]!, localWallTime[2]!);
  const instant = new Date(event.start);
  if (Number.isNaN(instant.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(instant);
  } catch {
    return "";
  }
}

export function calendarCellEventMeta(event: Pick<WorkCalendarEvent, "start" | "allDay" | "extendedProps">, timeZone: string): string {
  const time = calendarEventTimeLabel(event, timeZone);
  return [event.extendedProps.client.trim(), event.extendedProps.owner.trim(), time].filter(Boolean).join(" · ");
}

export function resetEmbeddedToolScroll(root: HTMLElement | null, embedded: boolean): void {
  if (!embedded || !root) return;
  // WHY [BOL-TOOL-SCROLL-RESET-001]: Board and Calendar reuse one contextual
  // surface. A switch must begin at Calendar controls, not inherit Board's old
  // depth. Retain drawer fallback for legacy embeds. Falsifier: Calendar opens
  // below its month controls after scrolling Board.
  const scrollOwner = root.closest<HTMLElement>(".ops-surface__body")
    ?? root.closest<HTMLElement>(".drawer__body");
  if (scrollOwner) scrollOwner.scrollTop = 0;
}

export function calendarFilteredItems(board: BoardSnapshot, filter: CalendarFilter): WorkItem[] {
  const scheduledByWork = new Map((board.workAgentStates ?? [])
    .filter((state) => state.stage === "SCHEDULED" && state.scheduledAt)
    .map((state) => [state.workItemId, state.scheduledAt!]));
  const projectedWorkIds = new Set(opsCalendarProjection({
    workItems: board.workItems,
    workAgentStates: board.workAgentStates ?? [],
    workspaceTimeZone: board.workspace.timezone,
    scope: filter
  }).map((entry) => entry.workItemId));
  return board.workItems
    .filter((item) => projectedWorkIds.has(item.id))
    .sort((left, right) => calendarSortTime(left, filter, scheduledByWork) - calendarSortTime(right, filter, scheduledByWork)
      || left.title.localeCompare(right.title));
}

export function defaultCalendarViewContext(timezone: string, now = new Date()): CalendarViewContext {
  const selectedDay = workspaceDayKey(now, timezone);
  const monthStart = `${selectedDay.slice(0, 7)}-01`;
  const nextMonth = new Date(`${monthStart}T12:00:00.000Z`);
  nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
  return {
    calendarScope: "ALL_SCHEDULED",
    visibleStartDay: monthStart,
    visibleEndDayExclusive: nextMonth.toISOString().slice(0, 10),
    selectedDay,
    focusedWorkItemId: null
  };
}

export function calendarViewContextForDay(current: CalendarViewContext, dayKey: string): CalendarViewContext {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(dayKey)) return current;
  const exactDay = new Date(`${dayKey}T12:00:00.000Z`);
  if (Number.isNaN(exactDay.getTime()) || exactDay.toISOString().slice(0, 10) !== dayKey) return current;
  const monthStart = `${dayKey.slice(0, 7)}-01`;
  const nextMonth = new Date(`${monthStart}T12:00:00.000Z`);
  if (Number.isNaN(nextMonth.getTime())) return current;
  nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
  return {
    ...current,
    visibleStartDay: monthStart,
    visibleEndDayExclusive: nextMonth.toISOString().slice(0, 10),
    selectedDay: dayKey,
    focusedWorkItemId: null
  };
}

export function CalendarView({ board, openWork, addWorkForDay, onNavigate, onContextChange, initialContext: suppliedInitialContext, embedded = false }: {
  board: BoardSnapshot;
  openWork: (item: WorkItem) => void;
  addWorkForDay?: (dayKey: string) => void;
  onNavigate?: () => void;
  onContextChange?: (context: CalendarViewContext) => void;
  initialContext?: CalendarViewContext;
  embedded?: boolean;
}) {
  const initialContext = useMemo(
    () => suppliedInitialContext ?? defaultCalendarViewContext(board.workspace.timezone),
    [board.workspace.timezone, suppliedInitialContext]
  );
  const [filter, setFilter] = useState<CalendarFilter>(initialContext.calendarScope);
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches);
  const [selectedDay, setSelectedDay] = useState(initialContext.selectedDay);
  const [focusedWorkItemId, setFocusedWorkItemId] = useState<string | null>(initialContext.focusedWorkItemId);
  const [calendarAnchor, setCalendarAnchor] = useState(initialContext.visibleStartDay);
  const [visibleRange, setVisibleRange] = useState(() => ({
    start: initialContext.visibleStartDay,
    end: initialContext.visibleEndDayExclusive
  }));
  const calendarViewRef = useRef<HTMLDivElement | null>(null);
  const attachCalendarView = useCallback((node: HTMLDivElement | null) => {
    calendarViewRef.current = node;
    resetEmbeddedToolScroll(node, embedded);
  }, [embedded]);
  const syncCompactCalendarDay = useCallback((dayKey: string) => {
    const cells = calendarViewRef.current?.querySelectorAll<HTMLElement>("[data-calendar-day='true']") ?? [];
    cells.forEach((cell) => {
      const selected = cell.getAttribute("data-date") === dayKey;
      cell.tabIndex = selected ? 0 : -1;
      cell.setAttribute("aria-selected", String(selected));
    });
  }, []);
  const selectCalendarDay = useCallback((dayKey: string) => {
    setSelectedDay(dayKey);
    setFocusedWorkItemId(null);
    syncCompactCalendarDay(dayKey);
  }, [syncCompactCalendarDay]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = window.matchMedia("(max-width: 700px)");
    const change = () => setCompact(query.matches);
    change();
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);

  useEffect(() => {
    onContextChange?.({
      calendarScope: filter,
      visibleStartDay: visibleRange.start,
      visibleEndDayExclusive: visibleRange.end,
      selectedDay,
      focusedWorkItemId
    });
  }, [filter, focusedWorkItemId, onContextChange, selectedDay, visibleRange.end, visibleRange.start]);

  const active = useMemo(() => board.workItems.filter((item) => ACTIVE_CALENDAR_STATUSES.has(item.status)), [board.workItems]);
  const now = new Date();
  const counts: Record<CalendarFilter, number> = {
    ALL_SCHEDULED: calendarEvents(board, "ALL_SCHEDULED", now).length,
    ENGAGEMENTS: calendarEvents(board, "ENGAGEMENTS", now).length,
    TASK_DEADLINES: calendarEvents(board, "TASK_DEADLINES", now).length,
    FOLLOW_UPS: calendarEvents(board, "FOLLOW_UPS", now).length,
    SCHEDULED_MESSAGES: calendarEvents(board, "SCHEDULED_MESSAGES", now).length
  };
  const events = calendarEvents(board, filter, now);
  const renderKey = calendarRenderKey(compact, events);
  const viewConfiguration = calendarViewConfiguration(compact);
  const itemById = useMemo(() => new Map(active.map((item) => [item.id, item])), [active]);
  const selectedDayEvents = calendarEventsForDay(events, selectedDay);
  useEffect(() => {
    if (focusedWorkItemId && !itemById.has(focusedWorkItemId)) setFocusedWorkItemId(null);
  }, [focusedWorkItemId, itemById]);
  useEffect(() => {
    if (compact) syncCompactCalendarDay(selectedDay);
  }, [compact, renderKey, selectedDay, syncCompactCalendarDay]);

  // WHY [BOL-CALENDAR-SAME-WORK-002]: this is a projection of exact Work, never
  // a second calendar database. Structured engagements, deadlines, follow-ups,
  // and scheduled messages are separate truthful scopes; title words never
  // decide what an event means. Every entry opens the same Work record.
  // Falsifier: “edit shoot footage” becomes a shoot, an actual engagement is
  // absent, an ALL entry is not backed by a structured date, or an edit changes
  // only one view after reload.
  return (
    <div ref={attachCalendarView} className={`view-stack calendar-view ${embedded ? "view-stack--embedded-tool" : ""}`}>
      {!embedded ? <PageHeading eyebrow="Calendar" title="Schedule" copy="Scheduled engagements are separate from task deadlines, follow-ups, and messages. Every entry opens the same Work record." /> : null}
      {embedded ? <label className="calendar-compact-filter"><span>Show</span><select aria-label="Filter calendar" value={filter} onChange={(event) => { setFilter(event.target.value as CalendarFilter); setFocusedWorkItemId(null); }}><option value="ALL_SCHEDULED">All scheduled</option><option value="ENGAGEMENTS">Meetings & shoots</option><option value="TASK_DEADLINES">Task deadlines</option><option value="FOLLOW_UPS">Follow-ups</option><option value="SCHEDULED_MESSAGES">Scheduled messages</option></select></label> : <section className="calendar-summary" aria-label="Calendar filters">
        {([
          ["ALL_SCHEDULED", "All scheduled"],
          ["ENGAGEMENTS", "Meetings & shoots"],
          ["TASK_DEADLINES", "Deadlines"],
          ["FOLLOW_UPS", "Follow-ups"],
          ["SCHEDULED_MESSAGES", "Messages"]
        ] as const).map(([value, label]) => <button key={value} type="button" aria-pressed={filter === value} className={filter === value ? "is-active" : ""} onClick={() => { setFilter(value); setFocusedWorkItemId(null); }}><strong>{counts[value]}</strong><span>{label}</span></button>)}
      </section>}
      {active.length === 0 ? <EmptyState>No active work is scheduled.</EmptyState> : <section className="calendar-workspace" aria-label="Work schedule">
        <div
          className="calendar-surface"
          onClickCapture={(event) => {
            // WHY [BOL-CALENDAR-DAY-CLICK-001]: the live FullCalendar v7 grid
            // emitted real clicks without invoking its interaction callbacks,
            // leaving the agenda stale and event cards inert. Own the rendered
            // data-date/data-work-id projection at our wrapper. Falsifier: tapping Aug 14 leaves the
            // agenda on Aug 13, or tapping an event does not open that Work.
            const dayKey = calendarDayKeyFromClickTarget(event.target);
            if (dayKey) selectCalendarDay(dayKey);
            const workId = calendarWorkIdFromClickTarget(event.target);
            const item = workId ? itemById.get(workId) : null;
            if (item) {
              setFocusedWorkItemId(item.id);
              openWork(item);
            }
          }}
          onKeyDownCapture={(event) => {
            if (!compact) return;
            const cell = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-calendar-day='true']");
            const dayKey = cell?.getAttribute("data-date") ?? null;
            if (!cell || !dayKey) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              selectCalendarDay(dayKey);
              return;
            }
            const nextDay = calendarDayKeyAfterArrow(dayKey, event.key);
            if (!nextDay) return;
            const nextCell = [...(calendarViewRef.current?.querySelectorAll<HTMLElement>("[data-calendar-day='true']") ?? [])]
              .find((candidate) => candidate.getAttribute("data-date") === nextDay);
            if (!nextCell) return;
            event.preventDefault();
            selectCalendarDay(nextDay);
            nextCell.focus();
          }}
        >
          {events.length === 0 ? <p className="calendar-filter-empty">Nothing is scheduled in this view yet.</p> : null}
          {/* WHY [BOL-CALENDAR-MOUNTED-EVENTS-004]: the live FullCalendar v7
              absolute event harness left canonical Work visibility:hidden
              with no computed top. Keep its month/date/navigation shell, but
              render the same projection in dayCellTopContent normal flow and
              remount by the stable projection key. Never restore events={events}
              or an event source. Falsifier: agenda Work exists while its cell
              card/dot is hidden, stale, or opens a different Work drawer. */}
          <FullCalendar
          key={renderKey}
          plugins={[classicThemePlugin, dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
          initialView={viewConfiguration.initialView}
          initialDate={calendarAnchor}
          headerToolbar={viewConfiguration.headerToolbar}
          footerToolbar={viewConfiguration.footerToolbar}
          buttons={viewConfiguration.buttons}
          todayText="Today"
          monthText="Month"
          weekTextLong="Week"
          weekTextShort="Week"
          dayText="Day"
          listText="Agenda"
          className="ops-calendar"
          toolbarClass="ops-calendar__toolbar"
          toolbarSectionClass="ops-calendar__toolbar-section"
          toolbarTitleClass="ops-calendar__toolbar-title"
          buttonClass="ops-calendar__button"
          buttonGroupClass="ops-calendar__button-group"
          tableClass="ops-calendar__table"
          dayHeaderInnerClass="ops-calendar__day-header"
          dayCellClass={(info) => `ops-calendar__day-cell${info.isToday ? " is-today" : ""}${compact ? " ops-calendar__day-cell--interactive" : ""}`}
          dayCellInnerClass="ops-calendar__day-cell-inner"
          dayCellTopInnerClass="ops-calendar__day-top"
          dayCellTopContent={(info) => {
            const dayKey = workspaceDayKey(info.date, board.workspace.timezone);
            const { visibleEvents, overflowCount } = calendarDayProjection(events, dayKey, compact);
            // WHY [BOL-CALENDAR-V7-EVENT-001]: app-owned cell content carries
            // exact Work identity, readable evidence, and client color without
            // depending on FullCalendar's hashed/hidden event rows. Falsifier:
            // a cell action is unlabeled, uncolored, or opens different Work.
            return <div className={`ops-calendar-cell${compact ? " ops-calendar-cell--compact" : ""}`}>
              <span className="ops-calendar__day-number">{info.dayNumberText}</span>
              {visibleEvents.length > 0 ? compact
                ? <span className="ops-calendar-cell__dots" aria-hidden="true">{visibleEvents.map((event) => <ColorMark key={event.id} className="ops-calendar-cell__dot" color={event.extendedProps.color} />)}</span>
                : <span className="ops-calendar-cell__items">{visibleEvents.map((event) => {
                    const label = calendarEventLabel(event);
                    return <button
                      type="button"
                      key={event.id}
                      className="ops-calendar-cell__item"
                      data-kind={event.extendedProps.kind}
                      data-work-id={event.extendedProps.workId}
                      aria-label={label}
                      title={label}
                    >
                      <ColorMark className="ops-calendar-cell__mark" color={event.extendedProps.color} />
                      <span><strong>{event.title}</strong><small>{calendarCellEventMeta(event, board.workspace.timezone)}</small></span>
                    </button>;
                  })}</span>
                : null}
              {overflowCount > 0 ? <span className="ops-calendar-cell__overflow" aria-hidden={compact || undefined}>+{overflowCount} more</span> : null}
            </div>;
          }}
          dayCellDidMount={(info) => {
            if (!compact) return;
            // WHY [BOL-CALENDAR-COMPACT-DAY-TARGET-001]: phone month events are
            // visual status marks, not 7px buttons. The containing grid day is
            // the one pointer/keyboard target; its agenda owns readable task
            // actions. Never make every tiny mark independently interactive.
            // Falsifier: Tab lands on a dot, a day cannot be selected with
            // Enter/Space/arrows, or the selected agenda differs from that day.
            const dayKey = info.el.getAttribute("data-date") ?? workspaceDayKey(info.date, board.workspace.timezone);
            const currentLabel = info.el.getAttribute("aria-label") ?? dayKey;
            info.el.setAttribute("data-calendar-day", "true");
            info.el.setAttribute("aria-label", `${currentLabel}. Select day to view work.`);
            info.el.setAttribute("aria-selected", String(dayKey === selectedDay));
            info.el.tabIndex = dayKey === selectedDay ? 0 : -1;
          }}
          datesSet={(info) => {
            setFocusedWorkItemId(null);
            const nextAnchor = workspaceDayKey(info.view.currentStart, board.workspace.timezone);
            setCalendarAnchor((current) => current === nextAnchor ? current : nextAnchor);
            const visibleStart = workspaceDayKey(info.start, board.workspace.timezone);
            const currentEnd = workspaceDayKey(info.end, board.workspace.timezone);
            setVisibleRange((current) => current.start === visibleStart && current.end === currentEnd
              ? current
              : { start: visibleStart, end: currentEnd });
            setSelectedDay((current) => current >= nextAnchor && current < currentEnd ? current : nextAnchor);
            onNavigate?.();
          }}
          dateClick={(info) => selectCalendarDay(info.dateStr.slice(0, 10))}
          height="auto"
          nowIndicator
          navLinks={false}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          scrollTime="07:00:00"
          />
        </div>
        <CalendarDayAgenda dayKey={selectedDay} events={selectedDayEvents} itemById={itemById} openWork={(item) => { setFocusedWorkItemId(item.id); openWork(item); }} addWork={addWorkForDay} />
      </section>}
    </div>
  );
}

function CalendarDayAgenda({ dayKey, events, itemById, openWork, addWork }: {
  dayKey: string;
  events: readonly WorkCalendarEvent[];
  itemById: ReadonlyMap<string, WorkItem>;
  openWork: (item: WorkItem) => void;
  addWork: ((dayKey: string) => void) | undefined;
}) {
  const label = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" }).format(new Date(`${dayKey}T12:00:00`));
  return (
    <aside className="calendar-day-agenda" aria-label={`${label} agenda`}>
      <header><div><small>Selected day</small><h3>{label}</h3></div><span>{addWork ? <button type="button" onClick={() => addWork(dayKey)}><Plus size={15} aria-hidden="true" />Add</button> : null}<b>{events.length}</b></span></header>
      {events.length === 0 ? <p>Nothing in this Calendar view on this day.</p> : <div className="calendar-day-agenda__items">{events.map((event) => {
        const item = itemById.get(event.extendedProps.workId);
        return <button type="button" key={event.id} disabled={!item} onClick={() => { if (item) openWork(item); }}>
          <ColorMark className="calendar-day-agenda__mark" color={event.extendedProps.color} />
          <span><strong>{event.title}</strong><small>{event.extendedProps.client} · {event.extendedProps.owner}</small></span>
          <em>{event.extendedProps.kind === "ENGAGEMENT" ? "Scheduled" : event.extendedProps.kind === "DUE" ? "Due" : event.extendedProps.kind === "CHECKPOINT" ? "Follow up" : "Message"}</em>
          <ArrowRight size={16} aria-hidden="true" />
        </button>;
      })}</div>}
    </aside>
  );
}

export function ClientsView({ board, openWork }: { board: BoardSnapshot; openWork: (item: WorkItem) => void }) {
  return (
    <div className="view-stack">
      <PageHeading eyebrow="Workspace" title="Client work" copy="Projects and active work, grouped by client." />
      <div className="client-grid">{board.clients.map((client) => {
        const work = board.workItems.filter((item) => item.clientId === client.id && item.status !== "DONE" && item.status !== "ARCHIVED");
        const projects = board.projects.filter((project) => project.clientId === client.id);
        return <section className="client-card" key={client.id}><header><ColorMark className="client-card__mark" color={client.color}>{client.name.slice(0, 1).toUpperCase()}</ColorMark><div><h2>{client.name}</h2><p>{work.length} active · {projects.length} projects</p></div></header><div className="client-card__projects">{projects.map((project) => <span key={project.id}>{project.name}</span>)}</div><div className="client-card__work">{work.slice(0, 3).map((item) => <button type="button" key={item.id} onClick={() => openWork(item)}><span>{item.title}</span><ArrowRight size={15} /></button>)}{work.length === 0 ? <p>No active work.</p> : null}</div></section>;
      })}</div>
    </div>
  );
}

export function ActivityView({ afterUndo, notify }: { afterUndo: () => Promise<void>; notify: (text: string, tone?: "success" | "error") => void }) {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const load = () => api.activity().then((response) => setEvents(response.events)).catch(() => setEvents([]));
  useEffect(() => { void load(); }, []);
  const undo = async (event: ActivityEvent) => {
    try { await api.undoActivity(event.id); await Promise.all([load(), afterUndo()]); notify("Change undone."); }
    catch (error) { notify(error instanceof Error ? error.message : "That change could not be undone.", "error"); }
  };
  return (
    <div className="view-stack">
      <PageHeading eyebrow="Audit trail" title="What changed" copy="Every saved change, with undo where it is safe." />
      {events === null ? <p className="loading-line">Loading activity.</p> : events.length === 0 ? <EmptyState>No activity has been recorded yet.</EmptyState> : <section className="activity-feed">{events.map((event) => <article key={event.id}><span className={`activity-icon activity-icon--${event.actorType.toLowerCase()}`}>{event.actorType === "HUMAN" ? <UsersRound size={17} /> : event.actorType === "AGENT" ? <ShieldCheck size={17} /> : <Activity size={17} />}</span><div><strong>{readableAction(event.action)}</strong><p>{event.reason}</p>{event.whatsappDeliveryState ? <span className={`agent-delivery-badge agent-delivery-badge--${event.whatsappDeliveryState.toLowerCase()}`} aria-label={`WhatsApp delivery: ${activityDeliveryLabel(event.whatsappDeliveryState)}`}>{activityDeliveryLabel(event.whatsappDeliveryState)}</span> : null}<small>{event.actorType.toLowerCase()} · {formatTimeAgo(event.createdAt)}</small></div>{event.reversible ? <button className="undo-button" type="button" onClick={() => void undo(event)}>Undo</button> : null}</article>)}</section>}
    </div>
  );
}

export function activityDeliveryLabel(state: NonNullable<ActivityEvent["whatsappDeliveryState"]>): string {
  return state === "SENT" ? "Sent" : state === "DELIVERED" ? "Delivered" : state === "READ" ? "Read" : "Played";
}

export function SettingsView({ board, canConfigure, notify, openWhatsApp, afterProposalRestore, privateMode, setPrivateMode, onSwitchBusiness = () => undefined }: {
  board: BoardSnapshot;
  canConfigure: boolean;
  notify: (text: string, tone?: "success" | "error") => void;
  openWhatsApp: (() => void) | undefined;
  afterProposalRestore: () => Promise<void>;
  privateMode: boolean;
  setPrivateMode: (enabled: boolean) => void;
  onSwitchBusiness?: (businessSlug: string) => void;
}) {
  const [model, setModel] = useState<BusinessOperatingModelVersion | null>(null);
  const [businessDetailsOpen, setBusinessDetailsOpen] = useState(() => window.location.hash === "#settings/business-details");
  const businessDetailsRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (window.location.hash === "#settings/business-details") setBusinessDetailsOpen(true);
  }, []);
  const [modelState, setModelState] = useState<"LOADING" | "MISSING" | "READY" | "ERROR">("LOADING");
  const [modelWorkspaceId, setModelWorkspaceId] = useState(board.workspace.id);
  const currentWorkspaceId = useRef(board.workspace.id);
  currentWorkspaceId.current = board.workspace.id;
  const modelRequestId = useRef(0);
  const effectiveModelState = modelWorkspaceId === board.workspace.id ? modelState : "LOADING";
  useEffect(() => {
    if (effectiveModelState === "READY" && window.location.hash === "#settings/business-details") {
      businessDetailsRef.current?.scrollIntoView({ block: "start" });
      businessDetailsRef.current?.querySelector("summary")?.focus();
    }
  }, [effectiveModelState]);
  const [brain, setBrain] = useState<AgentBrainSettings | null>(null);
  const [brainSelection, setBrainSelection] = useState("");
  const [brainBusy, setBrainBusy] = useState(false);
  const [control, setControl] = useState<AgentControlState | null>(null);
  const [modeBusy, setModeBusy] = useState(false);
  const [rejected, setRejected] = useState<AgentProposal[] | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const canClearWhatsApp = board.viewer.role === "OWNER";
  const [clearPreflight, setClearPreflight] = useState<WhatsAppClearResyncPreflight | null>(null);
  const [clearPreviewState, setClearPreviewState] = useState<"IDLE" | "LOADING" | "READY" | "FAILED">("IDLE");
  const [clearConfirmation, setClearConfirmation] = useState("");
  const [googleWorkspace, setGoogleWorkspace] = useState<GoogleWorkspaceSettings | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const loadModel = useCallback(async () => {
    const requestId = ++modelRequestId.current;
    setModelWorkspaceId(board.workspace.id);
    setModelState("LOADING");
    try {
      const loaded = await api.businessModel();
      if (requestId !== modelRequestId.current) return;
      if (!businessModelResultIsCurrent(currentWorkspaceId.current, board.workspace.id, loaded)) {
        setModel(null);
        setModelState("ERROR");
        return;
      }
      setModel(loaded);
      setModelState("READY");
    } catch (error) {
      if (requestId !== modelRequestId.current) return;
      setModel(null);
      // WHY [BOL-FIRST-MODEL-GET-001]: Only the exact absent-model contract
      // opens first setup. A DB/transport/authorization failure must remain a
      // retryable error, never a fabricated empty workspace or endless loading.
      setModelState(businessModelFetchFailureState(error));
    }
  }, [board.workspace.id]);
  const loadBrain = useCallback(async () => {
    try {
      const loaded = await api.agentBrain();
      setBrain(loaded);
      setBrainSelection(agentBrainRouteKey(loaded.primary));
    } catch {
      setBrain(null);
      setBrainSelection("");
    }
  }, []);
  const loadRejected = useCallback(() => api.rejectedProposals()
    .then((response) => setRejected(response.proposals))
    .catch(() => setRejected([])), []);
  const loadClearPreflight = useCallback(async () => {
    if (!canClearWhatsApp) return;
    setClearPreviewState("LOADING");
    try {
      setClearPreflight(await api.whatsAppClearResyncPreflight());
      setClearPreviewState("READY");
    } catch {
      setClearPreflight(null);
      setClearPreviewState("FAILED");
    }
  }, [canClearWhatsApp]);
  useEffect(() => {
    void loadModel();
    void loadBrain();
    if (canConfigure) {
      void api.agentControl().then(setControl).catch(() => setControl(null));
      void api.googleWorkspaceSettings().then(setGoogleWorkspace).catch(() => setGoogleWorkspace({
        state: "UNAVAILABLE",
        account: null,
        grantedCapabilities: [],
        missingCapabilities: ["SELECTED_FILE_READ", "MEET_TRANSCRIPT_READ", "CALENDAR_CONTEXT_READ"],
        broadDriveDiscovery: "NOT_REQUESTED",
        canConnect: false,
        canDisconnect: false,
        unavailableReason: "Google Workspace settings could not be checked.",
        updatedAt: null
      }));
      void loadRejected();
      if (canClearWhatsApp) void loadClearPreflight();
    }
    return () => { modelRequestId.current++; };
  }, [canClearWhatsApp, canConfigure, loadBrain, loadClearPreflight, loadModel, loadRejected]);
  const changeMode = async (selection: "MANUAL" | "AUTO") => {
    if (!control || control.selection === selection) return;
    if (selection === "AUTO" && !window.confirm("Turn on only the action classes already proven safe?")) return;
    setModeBusy(true);
    try {
      const saved = await api.updateAgentControl(selection, control.version);
      setControl(saved);
      notify(selection === "MANUAL" ? "Every agent action now waits for approval." : "Proven action classes now run automatically.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "The approval mode did not save.", "error");
      void api.agentControl().then(setControl).catch(() => setControl(null));
    } finally {
      setModeBusy(false);
    }
  };
  const restoreRejected = async (proposal: AgentProposal) => {
    setRestoringId(proposal.id);
    try {
      await api.restoreRejectedProposal(proposal.id, proposal.version);
      setRejected((current) => current?.filter((item) => item.id !== proposal.id) ?? []);
      await afterProposalRestore();
      notify("Restored to Approvals. Nothing was applied or sent.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "That rejected item could not be restored.", "error");
      void loadRejected();
    } finally {
      setRestoringId(null);
    }
  };
  const changeBrain = async () => {
    if (!brain || brainBusy) return;
    const update = agentBrainUpdateForSelection(brain, brainSelection);
    if (!update || agentBrainRouteKey(brain.primary) === brainSelection) return;
    setBrainBusy(true);
    try {
      const saved = await api.updateAgentBrain(update);
      setBrain(saved);
      setBrainSelection(agentBrainRouteKey(saved.primary));
      notify(`Agent work model changed to ${saved.primary.label}.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "The agent work model did not save.", "error");
      await loadBrain();
    } finally {
      setBrainBusy(false);
    }
  };
  const connectGoogleWorkspace = async () => {
    setGoogleBusy(true);
    try {
      const result = await api.connectGoogleWorkspace();
      const authorizationUrl = safeGoogleAuthorizationUrl(result.authorizationUrl);
      if (!authorizationUrl) throw new Error("Google returned an unsafe authorization link.");
      window.location.assign(authorizationUrl);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Google Workspace could not be connected.", "error");
      void api.googleWorkspaceSettings().then(setGoogleWorkspace).catch(() => undefined);
      setGoogleBusy(false);
    }
  };
  const disconnectGoogleWorkspace = async () => {
    if (!window.confirm("Disconnect this Google account from this workspace?")) return;
    setGoogleBusy(true);
    try {
      const settings = await api.disconnectGoogleWorkspace();
      setGoogleWorkspace(settings);
      notify("Google Workspace disconnected. No Google file or event was changed.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Google Workspace could not be disconnected.", "error");
    } finally {
      setGoogleBusy(false);
    }
  };
  return (
    <div className="view-stack rules-view">
      <PageHeading eyebrow="Business settings" title="Settings" copy="Manage this business, account access, and the manager's operating rules." />
      <ProductWorkspaceSettings workspaceId={board.workspace.id} members={board.members} canConfigure={canConfigure} onSwitchBusiness={onSwitchBusiness} />
      <section className={"privacy-settings-card " + (privateMode ? "is-active" : "")} aria-label="Private mode settings">
        <span className="privacy-settings-card__icon">{privateMode ? <EyeOff size={22} aria-hidden="true" /> : <Eye size={22} aria-hidden="true" />}</span>
        <div><p className="eyebrow">Screen privacy</p><h2>Private mode</h2><small>Mask personal phone numbers in the app and copied text. Records, form values, destinations, and sends stay unchanged.</small></div>
        <button type="button" role="switch" aria-checked={privateMode} onClick={() => setPrivateMode(!privateMode)}><span>{privateMode ? "On" : "Off"}</span></button>
      </section>
      {effectiveModelState === "LOADING" ? <p className="loading-line" role="status">Loading the business profile.</p> : null}
      {effectiveModelState === "ERROR" ? <section className="first-setup__notice" role="alert"><h2>Business profile unavailable</h2><p>We could not check the saved profile. No setup or change has been made.</p><button className="secondary-button" type="button" onClick={() => void loadModel()}>Retry profile check</button></section> : null}
      {effectiveModelState === "MISSING" ? canConfigure
        ? <BusinessFirstSetup key={board.workspace.id} workspaceName={board.workspace.name} workspaceTimezone={board.workspace.timezone} onCreated={(saved) => { if (!businessModelResultIsCurrent(currentWorkspaceId.current, board.workspace.id, saved)) return; modelRequestId.current++; setModel(saved); setModelWorkspaceId(board.workspace.id); setModelState("READY"); const savedWorkspaceId = board.workspace.id; void announceFirstSetupSaved({ readBrain: api.agentBrain, stillCurrent: () => currentWorkspaceId.current === savedWorkspaceId, applyBrain: (loaded) => { setBrain(loaded); setBrainSelection(agentBrainRouteKey(loaded.primary)); }, notify }); }} onExisting={(current) => { if (!businessModelResultIsCurrent(currentWorkspaceId.current, board.workspace.id, current)) return; modelRequestId.current++; setModel(current); setModelWorkspaceId(board.workspace.id); setModelState("READY"); }} />
        : <section className="first-setup__notice" role="status"><h2>Business profile not set up</h2><p>An owner or manager must enter this business’s operating details before Luna can use them.</p></section> : null}
      {effectiveModelState === "READY" && model?.workspaceId === board.workspace.id ? <section className="role-banner"><span className="role-banner__icon"><ShieldCheck size={23} /></span><div><p className="eyebrow">Its job</p><h2>{model.model.agentRole.purpose}</h2><p>WhatsApp messages cannot change these rules.</p></div><span className="version-stamp">v{model.version}</span></section> : null}
      {canConfigure && effectiveModelState === "READY" && model?.workspaceId === board.workspace.id && "configuration" in model.model && model.model.configuration === "STARTER" ? <details ref={businessDetailsRef} id="business-details" className="first-setup__notice" open={businessDetailsOpen} onToggle={(event) => setBusinessDetailsOpen(event.currentTarget.open)}><summary>Business details (optional)</summary><BusinessFirstSetup key={model.id} workspaceName={board.workspace.name} workspaceTimezone={board.workspace.timezone} initialModel={model} onCreated={() => { void loadModel(); }} onExisting={() => { void loadModel(); }} /></details> : null}
      <SummaryDeliverySettings workspaceId={board.workspace.id} isOwner={board.viewer.role === "OWNER"} />
      <AgentBrainSelector
        brain={brain}
        selectedKey={brainSelection}
        busy={brainBusy}
        canConfigure={canConfigure}
        onSelect={setBrainSelection}
        onSave={() => void changeBrain()}
      />
      {canConfigure ? <section className="google-workspace-settings" aria-label="Google Workspace connection">
        <span className="google-workspace-settings__icon"><Cloud size={22} /></span>
        <div className="google-workspace-settings__body">
          <p className="eyebrow">Google Workspace</p>
          <h2>{googleWorkspaceStateLabel(googleWorkspace)}</h2>
          <p>{googleWorkspace?.account
            ? `${googleWorkspace.account.email} · ${googleWorkspaceCapabilitySummary(googleWorkspace)}`
            : googleWorkspace?.unavailableReason ?? "Checking Drive, Docs, Sheets, Calendar, and Meet."}</p>
          <small>Only selected Drive files—including Docs and Sheets—Meet transcripts, and read-only Calendar context are requested. Whole-Drive access stays off.</small>
        </div>
        {googleWorkspace?.canConnect ? <button className="secondary-button" type="button" disabled={googleBusy} onClick={() => void connectGoogleWorkspace()}>{googleBusy ? <LoaderCircle className="spin" size={16} /> : <Cloud size={16} />}{googleBusy ? "Opening Google" : googleWorkspace.account ? "Reconnect" : "Connect"}</button> : null}
        {googleWorkspace?.canDisconnect ? <button className="undo-button" type="button" disabled={googleBusy} onClick={() => void disconnectGoogleWorkspace()}>{googleBusy ? <LoaderCircle className="spin" size={15} /> : null}Disconnect</button> : null}
      </section> : null}
      {openWhatsApp ? <button className="connection-settings-link" type="button" onClick={openWhatsApp}><span><MessageCircleMore size={21} /></span><div><strong>WhatsApp</strong><small>Connect the business number and match WhatsApp people to your team.</small></div><ArrowRight size={19} /></button> : null}
      {canClearWhatsApp ? <WhatsAppClearResyncCard
        preflight={clearPreflight}
        state={clearPreviewState}
        confirmation={clearConfirmation}
        onConfirmation={setClearConfirmation}
        onRefresh={() => void loadClearPreflight()}
      /> : null}
      {canConfigure ? <section className="settings-rejected-card" aria-label="Rejected approvals">
        <header><span><RotateCcw size={19} /><strong>Rejected</strong></span><small>{rejected?.length ?? "—"}</small></header>
        {/* WHY [BOL-REJECTED-RECOVERY-UI-001]: Reject must clear the daily
            queue without acting like permanent deletion. Recovery lives in
            Settings, one item at a time; Undo only returns the exact still-safe
            revision to Approvals and never means approve/send/apply.
            Falsifier: Reject leaves the card in the queue, Undo has bulk scope,
            or the UI claims the underlying action was performed. */}
        {rejected === null ? <p className="loading-line">Loading rejected items.</p>
          : rejected.length === 0 ? <p className="settings-rejected-empty">No rejected items.</p>
            : <div className="settings-rejected-list">{rejected.map((proposal) => <article key={proposal.id}>
              <div><strong>{proposal.title}</strong><p>{proposal.whyNow}</p><small>{proposal.type.toLowerCase().replaceAll("_", " ")} · {formatTimeAgo(proposal.updatedAt)}</small></div>
              <button className="undo-button" type="button" disabled={restoringId !== null} onClick={() => void restoreRejected(proposal)}>{restoringId === proposal.id ? <LoaderCircle className="spin" size={15} /> : <RotateCcw size={15} />}{restoringId === proposal.id ? "Restoring" : "Undo"}</button>
            </article>)}</div>}
      </section> : null}
      <section className="settings-disclosures" aria-label="Business rules">
        {canConfigure ? <RuleDisclosure title={`Approval mode · ${control?.selection === "AUTO" ? "Auto" : "Manual"}`} icon={ShieldCheck}><div className="settings-agent-mode"><div className="agent-mode-switch" role="group" aria-label="Manual or Auto"><button type="button" aria-pressed={control?.selection === "MANUAL"} className={control?.selection === "MANUAL" ? "is-active" : ""} disabled={!control || modeBusy} onClick={() => void changeMode("MANUAL")}>Manual</button><button type="button" aria-pressed={control?.selection === "AUTO"} className={control?.selection === "AUTO" ? "is-active" : ""} disabled={!control?.autoAvailable || modeBusy} onClick={() => void changeMode("AUTO")}>Auto</button></div><span className="transport-lock"><LockKeyhole size={15} />{control?.transportMode === "APPROVED_ONLY" ? "Approved sends only" : control?.transportMode === "POLICY_AUTO" ? "Policy auto" : "WhatsApp sending locked"}</span><p>Manual keeps every proposed board change and message behind approval. Send authority stays separate.</p></div></RuleDisclosure> : null}
        {effectiveModelState === "READY" && model?.workspaceId === board.workspace.id ? <><RuleDisclosure title="What the manager owns" icon={CheckCircle2}><ul>{model.model.agentRole.responsibilities.map((item) => <li key={item}>{item}</li>)}</ul></RuleDisclosure>
        <RuleDisclosure title="What it will never do" icon={CircleAlert} tone="guard"><ul>{model.model.agentRole.prohibitedBehaviors.map((item) => <li key={item}>{item}</li>)}</ul></RuleDisclosure>
        <RuleDisclosure title="Team roles" icon={UsersRound}>{model.model.responsibilities.map((role) => <div className="role-row" key={role.roleKey}><strong>{role.name}</strong><span>{role.owns.slice(0, 2).join(" · ")}</span></div>)}</RuleDisclosure>
        <RuleDisclosure title="Workflow definitions" icon={CalendarDays}>{model.model.workflowStages.map((stage, index) => <div className="workflow-row" key={stage.key}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{stage.label}</strong><small>{stage.definitionOfDone[0]}</small></div></div>)}</RuleDisclosure></> : null}
      </section>
      {effectiveModelState === "READY" && model?.workspaceId === board.workspace.id ? <p className="model-footnote">{board.workspace.name} · {board.members.length} active people · model v{model.version}</p> : null}
    </div>
  );
}

export function agentBrainRouteKey(
  route: Pick<AgentBrainSettings["primary"], "provider" | "model">
): string {
  return `${route.provider}/${route.model}`;
}

export function agentBrainUpdateForSelection(
  settings: AgentBrainSettings,
  selectedKey: string
): UpdateAgentBrainSettings | null {
  const selected = settings.selectablePrimaries.find(
    (route) => agentBrainRouteKey(route) === selectedKey
  );
  if (!selected) return null;
  const parsed = UpdateAgentBrainSettingsSchema.safeParse({
    provider: selected.provider,
    model: selected.model,
    expectedVersion: settings.version
  });
  return parsed.success ? parsed.data : null;
}

export function AgentBrainSelector({ brain, selectedKey, busy, canConfigure, onSelect, onSave }: {
  brain: AgentBrainSettings | null;
  selectedKey: string;
  busy: boolean;
  canConfigure: boolean;
  onSelect: (key: string) => void;
  onSave: () => void;
}) {
  const currentKey = brain ? agentBrainRouteKey(brain.primary) : "";
  const value = selectedKey || currentKey;
  const changed = Boolean(brain) && value !== currentKey;
  return <section className="brain-settings-card" aria-label="Manager brain">
    <span className="brain-settings-card__icon"><BrainCircuit size={22} /></span>
    <div className="brain-settings-card__identity"><p className="eyebrow">Agent work model</p><h2>{brain?.primary.label ?? "Loading"}</h2><small><i className={brain?.agentActive ? "status-dot status-dot--live" : "status-dot"} />{brain?.agentActive ? "Agent on" : "Agent paused"} · {brain?.executionMode === "AUTO" ? "Approved automations" : "Manual approval"}</small></div>
    <label>Choose agent work model
      <select aria-label="Choose agent work model" value={value} disabled={!brain || !canConfigure || busy} onChange={(event) => onSelect(event.target.value)}>
        {(brain?.selectablePrimaries ?? []).map((route) => <option key={agentBrainRouteKey(route)} value={agentBrainRouteKey(route)}>{route.label}</option>)}
      </select>
    </label>
    {canConfigure ? <button className="secondary-button" type="button" disabled={!changed || busy} onClick={onSave}>{busy ? "Saving" : "Use selected model"}</button> : null}
    <p>Requested task and draft work uses this model. Quick routing, Voice, and the separate final-answer route stay unchanged. There is no automatic fallback.</p>
  </section>;
}

export function whatsAppClearConfirmationReady(
  preflight: { available: boolean; confirmationPhrase: string } | null,
  confirmation: string
): boolean {
  return Boolean(preflight?.available) && confirmation === preflight?.confirmationPhrase;
}

export function WhatsAppClearResyncCard({ preflight, state, confirmation, onConfirmation, onRefresh }: {
  preflight: WhatsAppClearResyncPreflight | null;
  state: "IDLE" | "LOADING" | "READY" | "FAILED";
  confirmation: string;
  onConfirmation: (value: string) => void;
  onRefresh: () => void;
}) {
  const ready = whatsAppClearConfirmationReady(preflight, confirmation);
  const loading = state === "IDLE" || state === "LOADING";
  return (
    <section className="whatsapp-clear-card" aria-labelledby="whatsapp-clear-title">
      <header className="whatsapp-clear-card__header">
        <span className="whatsapp-clear-card__icon"><CircleAlert size={22} aria-hidden="true" /></span>
        <div>
          <p className="eyebrow">Scoped maintenance</p>
          <h2 id="whatsapp-clear-title">Clear WhatsApp data &amp; resync</h2>
          <p>Review the workspace, current connection, and blockers. Reconnecting never clears data.</p>
        </div>
        <span className="whatsapp-clear-card__status">Unavailable</span>
      </header>

      {!preflight ? (
        <div className={state === "FAILED" ? "whatsapp-clear-card__empty is-error" : "whatsapp-clear-card__empty"} role={state === "FAILED" ? "alert" : "status"}>
          {loading ? <LoaderCircle className="spin" size={21} aria-hidden="true" /> : <CircleAlert size={21} aria-hidden="true" />}
          <div>
            <strong>{loading ? "Loading the exact scope…" : "Preview unavailable. Nothing was cleared."}</strong>
            <p>{loading ? "No data changes while the preview loads." : "Refresh after the linked-account scope is available."}</p>
          </div>
          <button className="secondary-button" type="button" disabled={loading} onClick={onRefresh}>{loading ? "Loading" : "Try again"}</button>
        </div>
      ) : (
        <>
          <div className="whatsapp-clear-scope" aria-label="WhatsApp clear preflight scope">
            <article><small>Workspace</small><strong>{preflight.workspace.name}</strong><code>{preflight.workspace.slug} · {preflight.workspace.id}</code></article>
            <article><small>WhatsApp connection</small><strong>{preflight.account.label}</strong><span>{preflight.account.connectionStatus.toLocaleLowerCase().replaceAll("_", " ")} · all conversations · {preflight.account.bindingStatus === "SERVER_BOUND" ? "server-bound account fingerprint" : "account binding unavailable"}</span></article>
            <article className="whatsapp-clear-scope__fingerprint"><small>Account binding fingerprint</small><code>{preflight.account.bindingFingerprint ?? "Unavailable — execution blocked"}</code></article>
            <article className="whatsapp-clear-scope__fingerprint"><small>Preview summary fingerprint</small><code>{preflight.scopeFingerprint}</code></article>
          </div>

          <div className="whatsapp-clear-zero" role="status">
            <ShieldCheck size={21} aria-hidden="true" />
            <div><strong>Delete set: empty</strong><p>Nothing can be deleted until every dependency and a complete replacement sync are proven.</p></div>
            <span>{preflight.wouldDeleteNow.length} deletable</span>
          </div>

          <div className="whatsapp-clear-columns">
            <section aria-labelledby="whatsapp-clear-reviewed">
              <h3 id="whatsapp-clear-reviewed">Reviewed workspace artifacts</h3>
              <div className="whatsapp-clear-counts">{preflight.reviewedArtifacts.map((artifact) => <article key={artifact.key}><span>{artifact.label}</span><strong>{artifact.count.toLocaleString()}</strong></article>)}</div>
            </section>
            <section aria-labelledby="whatsapp-clear-guards">
              <h3 id="whatsapp-clear-guards">Authority guards</h3>
              <dl className="whatsapp-clear-guard-counts">
                <div><dt>Pending approvals</dt><dd>{preflight.guards.pendingApprovals}</dd></div>
                <div><dt>In-flight effects</dt><dd>{preflight.guards.inFlightEffects}</dd></div>
                <div><dt>Pending source checks</dt><dd>{preflight.guards.pendingSourceEvaluations}</dd></div>
              </dl>
              <ul className="whatsapp-clear-blockers">{preflight.blockers.map((blocker) => <li key={blocker.code}>{blocker.message}</li>)}</ul>
            </section>
          </div>

          <details className="whatsapp-clear-contract" open>
            <summary>Data that stays untouched</summary>
            <ul>{preflight.preservedAuthorities.map((authority) => <li key={authority.key}>{authority.label}</li>)}</ul>
          </details>
          <details className="whatsapp-clear-contract">
            <summary>What must be proven before this can run</summary>
            <ol>{preflight.resyncContract.map((step) => <li key={step}>{step}</li>)}</ol>
          </details>

          <div className="whatsapp-clear-confirmation">
            <div>
              <label htmlFor="whatsapp-clear-confirmation">If this becomes available, type <code>{WHATSAPP_CLEAR_CONFIRMATION_PHRASE}</code> to approve this exact deletion</label>
              <input
                id="whatsapp-clear-confirmation"
                type="text"
                value={confirmation}
                disabled={!preflight.available}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => onConfirmation(event.target.value)}
              />
              <small>WhatsApp stays linked. Reconnect is a separate action and never authorizes deletion.</small>
            </div>
            <button className="whatsapp-clear-action" type="button" disabled={!ready}>Clear &amp; resync unavailable</button>
          </div>
          <footer className="whatsapp-clear-card__footer">
            <span>Previewed {formatClearPreviewTime(preflight.generatedAt)}</span>
            <button className="secondary-button" type="button" disabled={loading} onClick={onRefresh}>{loading ? <LoaderCircle className="spin" size={15} /> : <RotateCcw size={15} />}{loading ? "Refreshing" : "Refresh exact scope"}</button>
          </footer>
        </>
      )}
    </section>
  );
}

function formatClearPreviewTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "just now";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

export function googleWorkspaceStateLabel(settings: GoogleWorkspaceSettings | null): string {
  if (!settings) return "Checking connection";
  if (settings.state === "UNAVAILABLE") return "Not available yet";
  if (settings.state === "DISCONNECTED") return "Not connected";
  if (settings.state === "CONNECTING") return "Connecting";
  if (settings.state === "CHECK_REQUIRED") return "Connection check needed";
  if (settings.state === "REAUTH_REQUIRED") return "Reconnect required";
  if (settings.state === "SCOPE_UPGRADE_REQUIRED") return "More access needed";
  return "Connected";
}

export function googleWorkspaceCapabilitySummary(settings: GoogleWorkspaceSettings): string {
  const labels = [
    settings.grantedCapabilities.includes("SELECTED_FILE_READ") ? "selected files" : null,
    settings.grantedCapabilities.includes("MEET_TRANSCRIPT_READ") ? "Meet transcripts" : null,
    settings.grantedCapabilities.includes("CALENDAR_CONTEXT_READ") ? "Calendar context" : null
  ].filter((value): value is string => value !== null);
  return labels.length ? labels.join(" · ") : "no usable access yet";
}

function RuleDisclosure({ title, icon: Icon, tone, children }: {
  title: string;
  icon: typeof CheckCircle2;
  tone?: "guard";
  children: ReactNode;
}) {
  return <details className={`settings-disclosure ${tone ? `settings-disclosure--${tone}` : ""}`}><summary><span><Icon size={19} /><strong>{title}</strong></span><ChevronDown size={18} /></summary><div className="settings-disclosure__body">{children}</div></details>;
}

function PageHeading({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return <section className="page-heading"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{copy}</p></section>;
}

function readableAction(action: string): string {
  return action.toLowerCase().split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

function calendarSortTime(item: WorkItem, filter: CalendarFilter, scheduledByWork: ReadonlyMap<string, string>): number {
  if (filter === "ALL_SCHEDULED") {
    const values = [
      (item.workKind ?? "TASK") === "ENGAGEMENT" ? item.scheduledStartAt : null,
      item.dueAt,
      item.nextCheckpointAt,
      scheduledByWork.get(item.id) ?? null
    ].flatMap((value) => value ? [new Date(value).getTime()] : []).filter(Number.isFinite);
    return values.length ? Math.min(...values) : Number.MAX_SAFE_INTEGER;
  }
  const value = filter === "ENGAGEMENTS" ? item.scheduledStartAt
    : filter === "TASK_DEADLINES" ? item.dueAt
      : filter === "FOLLOW_UPS" ? item.nextCheckpointAt
        : scheduledByWork.get(item.id) ?? null;
  return value ? new Date(value).getTime() : Number.MAX_SAFE_INTEGER;
}

function workspaceDayKey(value: string | Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(typeof value === "string" ? new Date(value) : value);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function calendarEventSchedule(value: string, timezone: string): Pick<WorkCalendarEvent, "start" | "allDay"> {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
  const isEndOfDay = part("hour") === "23" && part("minute") === "59";

  // WHY [BOL-CALENDAR-EOD-VISIBLE-003]: a date-only business deadline is stored
  // as 23:59 in the workspace timezone. Rendering that as a timed event can hide
  // it below a shortened business-hours grid. Put it in FullCalendar's all-day
  // row while preserving exact-time work as timed events. Falsifier: a Monday EOD
  // Work item is absent from Month, Week, or Day after reload.
  return isEndOfDay
    ? { start: `${part("year")}-${part("month")}-${part("day")}`, allDay: true }
    : { start: value, allDay: false };
}

export function calendarWorkspaceTimedStart(value: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}:00`;
}
